import {
  ContactGuestSchema,
  GuestSchema,
  UserSchema,
} from '../definitions/schemas';
import { formatPhoneNumber } from './PhoneNumber';

export interface DisplayNameOptions {
  // use only the first name, regardless if the last name is present
  firstNameOnly?: boolean;
  // whether the full email must be used, instead of stripping out the domain
  fullEmail?: boolean;
  // whether a mask should be applied to the phone number
  // e.g.: from +1 (555) 555-5555 to (***) ***-5555
  maskPhoneNumber?: boolean;
  // prioritizes the mobile phone number over the email if both are present
  prioritizeMobilePhone?: boolean;
}

/**
 * Compose full name based on certain rules:
 * - Both components: first + last
 * - Only first: first (no empty space at the end)
 * - Only last: last (no empty space at the beginning)
 * - None: empty string
 */
export function composeFullname(
  guest?: Pick<GuestSchema, 'firstName' | 'lastName'>
) {
  const { firstName, lastName } = guest || {};
  const parts = [];

  if (firstName) {
    parts.push(firstName);
  }

  if (lastName) {
    parts.push(lastName);
  }

  return parts.join(' ');
}

/**
 * Builds a contact's (or user's) display name according to these rules:
 *   - Prioritize first name & first name present = return the first name
 *   - First & last name present = return the full name
 *   - No name, email present = return the email
 *   - No name or email, mobile phone present = return the mobile phone
 *   - No name, email and mobile phone present AND prioritize mobile phone
 *      = return the mobile phone instead of the email
 */

export const getDisplayName = (
  guest: GuestSchema,
  options: DisplayNameOptions = {}
): string => {
  const { firstNameOnly, fullEmail, maskPhoneNumber, prioritizeMobilePhone } =
    options;

  if (firstNameOnly && guest.firstName) {
    return guest.firstName;
  }

  let displayName = composeFullname(guest);
  if (displayName) {
    return displayName;
  }

  const { email, mobilePhone } = guest as ContactGuestSchema;

  if (email) {
    displayName = fullEmail ? email : email.split('@')[0];
  }

  if ((prioritizeMobilePhone && mobilePhone) || (!email && mobilePhone)) {
    displayName = formatPhoneNumber(mobilePhone, maskPhoneNumber);
  }

  return displayName;
};

/**
 * Compose an username based on certain rules:
 * - The composed full name if it's present
 * - Otherwise, return the username
 */
export const composeUsername = (user?: Partial<UserSchema>) =>
  composeFullname(user) || user?.username || '';

/**
 * Returns 2-character initials for avatars/placeholders.
 * Prefers first+last initial, then first two of first/last/email/username, or '?'.
 */
export function getInitials(
  firstName?: string,
  lastName?: string,
  email?: string,
  username?: string
): string {
  const first = (firstName ?? '').trim();
  const last = (lastName ?? '').trim();
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  if (last) return last.slice(0, 2).toUpperCase();
  if (email) return email.slice(0, 2).toUpperCase();
  if (username) return (username || '').trim().slice(0, 2).toUpperCase();
  return '?';
}
